import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "../css/Header.css";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "../context/LanguageContext";

function Header() {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible] = useState(true);
  const mobileNavRef = useRef(null);
  const lastScrollY = useRef(0);
  const scrollDirection = useRef('up');
  const scrollThreshold = useRef(0);

  const toggleMobileNav = useCallback(() => {
    if (isAnimating) return; // Prevent multiple clicks during animation
    
    setIsAnimating(true);
    
    if (isMobileNavOpen) {
      // Close animation
      const mobileNav = mobileNavRef.current;
      if (mobileNav) {
        mobileNav.classList.add('closing');
        setTimeout(() => {
          setIsMobileNavOpen(false);
          setIsAnimating(false);
        }, 50); // Match the duration of slideOut animation
      }
    } else {
      // Open animation
      setIsMobileNavOpen(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 50); // Match the duration of slideIn animation
    }
  }, [isAnimating, isMobileNavOpen]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Check if scrolled from top
      if (currentScrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Determine scroll direction
      const currentDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      
      // Handle direction change
      if (currentDirection !== scrollDirection.current) {
        scrollDirection.current = currentDirection;
        
        // Reset threshold counter when direction changes
        if (currentDirection === 'up') {
          scrollThreshold.current = 0;
        }
      }
      
      // Handle header visibility based on scroll direction
      // if (currentDirection === 'down' && currentScrollY > 120) {
      //   // Scrolling down & past header height - hide
      //   setIsVisible(false);
      //   scrollThreshold.current = 0;
      // } else if (currentDirection === 'up') {
      //   // When scrolling up, increment threshold counter
      //   scrollThreshold.current += 1;
        
      //   // Only show header after consistent upward scrolling (adjust the 5 value to control sensitivity)
      //   if (scrollThreshold.current > 5) {
      //     setIsVisible(true);
      //   }
      // }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Prevent background scrolling when mobile nav is open
  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobileNavOpen]);

  // Close mobile nav when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileNavOpen &&
        mobileNavRef.current &&
        !mobileNavRef.current.contains(event.target) &&
        !event.target.classList.contains("hamburger") &&
        !event.target.parentElement?.classList.contains("hamburger")
      ) {
        toggleMobileNav();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileNavOpen, toggleMobileNav]);

  const headerClass = `header ${isScrolled ? 'scrolled' : ''} ${isVisible ? 'visible' : 'hidden'}`;
  const { t } = useTranslation();
  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/overons", label: t("nav.overOns") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/reserveren", label: t("nav.reserveren") },
    { to: "/contact", label: t("nav.contact") }
  ];

  return (
    <header className={headerClass}>
      <div className="header-content">
        {/* 1) Desktop Navigation - only shown on large screens */}
        <div className="header-desktop-nav">
          <nav className="header-nav">
            <ul>
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* 2) Mobile Hamburger (for small screens) - now with 2 lines */}
        <div className="header-mobile-left">
          <div className={`hamburger ${isMobileNavOpen ? 'active' : ''}`} onClick={toggleMobileNav}>
            <span></span>
            <span></span>
          </div>
        </div>

        {/* 3) Logo (center) and Right Icons (socials, language, etc.) */}
        <div className="header-center">
          <Link to="/" className="header-logo">
            <img src="/assets/images/Logo.png" alt="Lamara Shisha Bar" />
          </Link>
        </div>

        <div className="header-icons">
          <div className="header-right">
            <div className="header-socials">
              <a
                href="https://www.facebook.com/groups/1734899870093801/"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook"></i>
              </a>
              <a
                href="https://www.instagram.com/lamaralounge"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <a
                href="https://wa.me/+31618601865"
                target="_blank"
                rel="noreferrer"
                aria-label="Whatsapp"
              >
                <i className="fab fa-whatsapp"></i>
              </a>
              <a
                href="https://www.tiktok.com/@lamaralounge"
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
              >
                <i className="fab fa-tiktok"></i>
              </a>
            </div>
            <LanguageSwitcher className="desktop-only" />
          </div>
        </div>
      </div>

      {/* 4) Mobile Navigation Overlay */}
      {isMobileNavOpen && (
          <div className="mobile-nav" ref={mobileNavRef}>
            <nav className="mobile-nav-menu">
              <ul>
                {navLinks.map((link) => (
                  <li key={`mobile-${link.to}`} onClick={toggleMobileNav}>
                    <Link to={link.to}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mobile-nav-language">
              <LanguageSwitcher className="mobile-only" />
            </div>
          </div>
        )}
      </header>
    );
  }

export default Header;
